import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotify } from "../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Badge,
  Radio,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
  PercentageOutlined,
  DollarOutlined,
  UserOutlined,
  GiftOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import promoService from "../../services/promoService";
import masterService from "../../services/masterService";
import {
  noWhitespaceValidator,
  createExpirationDateValidator,
  createDisabledExpirationDate,
} from "../../utils/commonFunctions";
import { useCommon } from "../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;
const DATE_FORMAT = "DD-MM-YYYY";
// Mirrors the backend rule: a normal (admin-typed) standalone promo code is
// capped at this many characters.
const PROMO_CODE_MAX_LENGTH = 20;
// Mirrors the backend rule: a normal promo code only has to be this long,
// PROMO_CODE_MAX_LENGTH is still its upper bound.
const PROMO_CODE_MIN_LENGTH = 3;

// Prisma Decimal serializes as { s, e, d } over JSON. Convert to a plain number
// regardless of whether the backend already normalized it.
const toNumberOrNull = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const n = parseFloat(value);
    return isNaN(n) ? null : n;
  }
  if (typeof value === "object") {
    if (typeof value.toString === "function") {
      const n = parseFloat(value.toString());
      if (!isNaN(n)) return n;
    }
    if (Array.isArray(value.d) && typeof value.e === "number" && typeof value.s === "number") {
      let digitStr = "";
      value.d.forEach((part, i) => {
        digitStr += i === 0 ? String(part) : String(part).padStart(7, "0");
      });
      const exponent = value.e - (digitStr.length - 1);
      return value.s * parseFloat(digitStr) * Math.pow(10, exponent);
    }
  }
  return null;
};

const PromoForm = () => {
  const navigate = useNavigate();
  const { promo_id } = useParams();
  const [form] = Form.useForm();
  const notify = useNotify();
  const { setPageTitle } = useCommon();

  const isEditMode = useMemo(
    () => promo_id && !isNaN(Number(promo_id)),
    [promo_id]
  );

  useEffect(() => {
    setPageTitle(isEditMode ? "Edit Promo" : "Add Promo");
  }, [isEditMode, setPageTitle]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  // Active plans / plan types for the dropdowns.
  const [plans, setPlans] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  // When editing a promo whose saved plan / plan type was later deactivated, it
  // won't be in the active lists above — keep a bare-id option so the admin can
  // still see (and knows it is deactivated) which one was selected.
  const [inactivePlanOption, setInactivePlanOption] = useState(null);
  const [inactivePlanTypeOption, setInactivePlanTypeOption] = useState(null);
  const [savedLimits, setSavedLimits] = useState({
    promo_per_user_limit: 0,
    promo_max_redeems: 0,
  });

  // Merge the deactivated placeholder (if any) into the active options.
  const planOptions = useMemo(
    () =>
      inactivePlanOption && !plans.some((p) => p.value === inactivePlanOption.value)
        ? [...plans, inactivePlanOption]
        : plans,
    [plans, inactivePlanOption]
  );
  const planTypeOptions = useMemo(
    () =>
      inactivePlanTypeOption && !planTypes.some((t) => t.value === inactivePlanTypeOption.value)
        ? [...planTypes, inactivePlanTypeOption]
        : planTypes,
    [planTypes, inactivePlanTypeOption]
  );

  // Drive conditional rendering based on promo type (discount_based | amount_based)
  const promoType = Form.useWatch("promo_type", form);
  const isDiscountBased = promoType === "discount_based";
  const isAmountBased = promoType === "amount_based";

  const fetchCountries = useCallback(async () => {
    try {
      const res = await masterService.getAllCountries({ isAll: true, status: "Y" });
      const data = res?.data?.data?.data ?? res?.data?.data ?? [];
      const list = Array.isArray(data)
        ? data.map((c) => ({ label: c.country_name, value: c.country_id }))
        : [];
      setCountries(list);
    } catch (e) {
      console.error("Error loading countries", e);
      notify.error({ message: "Failed to load countries" });
    }
  }, [notify]);

  const fetchPlanOptions = useCallback(async () => {
    try {
      const res = await masterService.getAssignSubscriptionOptions({});
      const data = res?.data?.data ?? {};
      setPlans((data.subscription_plans || []).map((p) => ({ label: p.title, value: p.id })));
      setPlanTypes((data.plan_types || []).map((t) => ({ label: t.name, value: t.id })));
    } catch (e) {
      console.error("Error loading plans / plan types", e);
      notify.error({ message: "Failed to load plans / plan types" });
    }
  }, [notify]);

  const fetchDetails = useCallback(async () => {
    if (!isEditMode) return;
    try {
      const res = await promoService.getPromo(promo_id);
      const api = res?.data;
      const payload = api?.data !== undefined ? api.data : api;
      let promo = null;
      if (Array.isArray(payload?.data)) promo = payload.data[0];
      else if (Array.isArray(payload)) promo = payload[0];
      else if (payload && typeof payload === "object")
        promo = payload.data || payload;

      if (!promo) {
        notify.error({ message: "Promo not found" });
        navigate("/promo");
        return;
      }

      // Campaign-generated promos can only be edited via their parent campaign.
      if (promo.is_generated && promo.pc_id != null) {
        notify.error({
          message:
            "This promo belongs to a campaign. Edit it from the campaign page instead.",
        });
        if (promo.pc_id) {
          navigate(`/promo-campaign/edit/${promo.pc_id}`);
        } else {
          navigate("/promo");
        }
        return;
      }

      setSavedLimits({
        promo_per_user_limit: promo.promo_per_user_limit ?? 0,
        promo_max_redeems: promo.promo_max_redeems ?? 0,
      });

      const fixedAmount = toNumberOrNull(promo.promo_fixed_amount);
      const derivedType = promo.discount_based
        ? "discount_based"
        : promo.amount_based
          ? "amount_based"
          : promo.promo_percent != null
            ? "discount_based"
            : fixedAmount != null
              ? "amount_based"
              : undefined;

      form.setFieldsValue({
        promo_name: promo.promo_name ?? undefined,
        promo_code: promo.promo_code ?? undefined,
        promo_description: promo.promo_description ?? undefined,
        promo_type: derivedType,
        promo_percent: promo.promo_percent ?? undefined,
        promo_min_txn_amount: toNumberOrNull(promo.promo_min_txn_amount) ?? undefined,
        promo_max_discount_amount:
          toNumberOrNull(promo.promo_max_discount_amount) ?? undefined,
        promo_fixed_amount: fixedAmount ?? undefined,
        promo_per_user_limit: promo.promo_per_user_limit ?? 0,
        promo_max_redeems: promo.promo_max_redeems ?? 0,
        target_audience: promo.target_audience ?? "ALL_USER",
        catelog_enabled: promo.catelog_enabled ?? false,
        country_ids: Array.isArray(promo.country_ids) ? promo.country_ids : [],
        fk_plan_id: promo.fk_plan_id ?? undefined,
        fk_plan_type_id: promo.fk_plan_type_id ?? undefined,
        start: promo.start ? dayjs(promo.start) : undefined,
        expiration: promo.expiration ? dayjs(promo.expiration) : undefined,
        is_active: promo.is_active ?? "Y",
      });

      // If the saved plan / plan type has since been deactivated (or deleted),
      // it won't appear in the active dropdown — surface just its id so the
      // admin can tell it was deactivated.
      if (promo.fk_plan_id != null && promo.plan && (!promo.plan.is_active || promo.plan.is_deleted)) {
        setInactivePlanOption({ label: `${promo.fk_plan_id} (inactive)`, value: promo.fk_plan_id });
      }
      if (
        promo.fk_plan_type_id != null &&
        promo.plan_type &&
        (!promo.plan_type.is_active || promo.plan_type.is_deleted)
      ) {
        setInactivePlanTypeOption({ label: `${promo.fk_plan_type_id} (inactive)`, value: promo.fk_plan_type_id });
      }
    } catch (e) {
      console.error("Error loading promo details", e);
      notify.error({ message: "Failed to load promo details" });
    }
  }, [form, isEditMode, navigate, notify, promo_id]);

  useEffect(() => {
    const init = async () => {
      setInitialLoading(true);
      await Promise.allSettled([fetchCountries(), fetchPlanOptions(), fetchDetails()]);
      setInitialLoading(false);
    };
    init();
  }, [fetchDetails, fetchCountries, fetchPlanOptions]);

  const onFinish = async (values) => {
    try {
      const discountBased = values.promo_type === "discount_based";
      const amountBased = values.promo_type === "amount_based";

      // --- Frontend validation gate (runs before the API call) ---
      if (!discountBased && !amountBased) {
        notify.error({ message: "Please select a promo type" });
        return;
      }

      const promoName = (values.promo_name || "").trim();
      if (!promoName) {
        notify.error({ message: "Promo name is required" });
        return;
      }

      const promoCode = (values.promo_code || "").trim();
      if (!promoCode) {
        notify.error({ message: "Promo code is required" });
        return;
      }
      if (/\s/.test(promoCode)) {
        notify.error({ message: "Promo code must be a single word (no spaces)" });
        return;
      }
      if (promoCode.length < PROMO_CODE_MIN_LENGTH || promoCode.length > PROMO_CODE_MAX_LENGTH) {
        notify.error({
          message: `Promo code must be between ${PROMO_CODE_MIN_LENGTH} and ${PROMO_CODE_MAX_LENGTH} characters`,
        });
        return;
      }

      const countryIds = Array.isArray(values.country_ids) ? values.country_ids : [];
      if (countryIds.length < 1) {
        notify.error({ message: "Please select at least one country" });
        return;
      }

      if (values.fk_plan_id == null) {
        notify.error({ message: "Please select a plan" });
        return;
      }
      if (values.fk_plan_type_id == null) {
        notify.error({ message: "Please select a plan type" });
        return;
      }

      if (!values.start || !values.expiration) {
        notify.error({ message: "Start and expiration dates are required" });
        return;
      }
      if (values.expiration.isBefore(values.start) || values.expiration.isSame(values.start)) {
        notify.error({ message: "Expiration date must be after start date" });
        return;
      }

      if (discountBased) {
        const pct = Number(values.promo_percent);
        if (values.promo_percent == null || values.promo_percent === "") {
          notify.error({ message: "Discount percentage is required" });
          return;
        }
        if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
          notify.error({ message: "Discount percentage must be between 1 and 100" });
          return;
        }
        const cap = Number(values.promo_max_discount_amount);
        if (values.promo_max_discount_amount != null && values.promo_max_discount_amount !== "" && (isNaN(cap) || cap < 0)) {
          notify.error({ message: "Max discount amount must be non-negative" });
          return;
        }
      }

      if (amountBased) {
        const fixed = Number(values.promo_fixed_amount);
        if (values.promo_fixed_amount == null || values.promo_fixed_amount === "") {
          notify.error({ message: "Fixed amount is required" });
          return;
        }
        if (!Number.isFinite(fixed) || fixed < 0) {
          notify.error({ message: "Fixed amount must be non-negative" });
          return;
        }
      }

      const minTxn = Number(values.promo_min_txn_amount);
      if (values.promo_min_txn_amount != null && values.promo_min_txn_amount !== "" && (isNaN(minTxn) || minTxn < 0)) {
        notify.error({ message: "Min transaction amount must be non-negative" });
        return;
      }

      const perUserLimit = Number(values.promo_per_user_limit);
      if (!Number.isFinite(perUserLimit) || perUserLimit <= 0) {
        notify.error({ message: "Per user limit must be greater than 0" });
        return;
      }
      if (isEditMode && perUserLimit < savedLimits.promo_per_user_limit) {
        notify.error({ message: `Per user limit can only be increased (current: ${savedLimits.promo_per_user_limit})` });
        return;
      }

      const maxRedeems = Number(values.promo_max_redeems);
      if (!Number.isFinite(maxRedeems) || maxRedeems <= 0) {
        notify.error({ message: "Max redeems must be greater than 0" });
        return;
      }
      if (isEditMode && maxRedeems < savedLimits.promo_max_redeems) {
        notify.error({ message: `Max redeems can only be increased (current: ${savedLimits.promo_max_redeems})` });
        return;
      }
      // --- End of frontend validation ---

      setLoading(true);
      const payload = {
        ...values,
        promo_id: isEditMode ? Number(promo_id) : undefined,
        discount_based: discountBased,
        amount_based: amountBased,
        promo_percent: discountBased ? (values.promo_percent ?? null) : null,
        promo_fixed_amount: amountBased
          ? (values.promo_fixed_amount ?? null)
          : null,
        // max discount cap is only for percentage promos
        promo_max_discount_amount: discountBased
          ? (values.promo_max_discount_amount ?? null)
          : null,
        promo_per_user_limit: values.promo_per_user_limit ?? 0,
        promo_max_redeems: values.promo_max_redeems ?? 0,
        start: values.start ? values.start.format(DATE_FORMAT) : null,
        expiration: values.expiration
          ? values.expiration.format(DATE_FORMAT)
          : null,
      };
      delete payload.promo_type;

      const res = await promoService.addEditPromo(payload);
      if (res?.data?.status) {
        notify.success({ message: res?.data?.message || "Success" });
        if (!isEditMode) form.resetFields();
        navigate("/promo");
      } else {
        notify.error({ message: res?.data?.message || "Not Saved" });
      }
    } catch (e) {
      console.error("Error saving promo", e);
      notify.error({ message: e?.response?.data?.message || e?.data?.message || "Failed to save promo" });
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => form.resetFields();
  const onGoBack = () => navigate("/promo");

  const validateExpirationDate = createExpirationDateValidator(form);
  const disabledExpirationDate = createDisabledExpirationDate(form);

  return (
    <div className="min-h-screen">
      <Card loading={initialLoading} className="shadow-lg border-0">
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={onFinish}
          initialValues={{
            is_active: "Y",
            country_ids: [],
            promo_per_user_limit: 1,
            promo_max_redeems: 1,
          }}
          className="space-y-6"
        >
          {/* Basic Information */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <GiftOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Basic Information
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={<span className="font-semibold">Promo Name</span>}
                  name="promo_name"
                  rules={[
                    { required: true, message: "Please enter a promo name" },
                    { validator: noWhitespaceValidator },
                  ]}
                >
                  <Input placeholder="Enter promo name" className="rounded-lg" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={<span className="font-semibold">Promo Code</span>}
                  name="promo_code"
                  extra={
                    isEditMode
                      ? "Promo code cannot be changed once set"
                      : `Min ${PROMO_CODE_MIN_LENGTH} characters · Max ${PROMO_CODE_MAX_LENGTH} characters`
                  }
                  rules={[
                    { required: true, message: "Please enter a promo code" },
                    { validator: noWhitespaceValidator },
                    {
                      validator: (_, value) => {
                        if (value == null || value === "") return Promise.resolve();
                        if (/\s/.test(String(value))) {
                          return Promise.reject(
                            new Error("Promo code must be a single word (no spaces)")
                          );
                        }
                        const trimmedLength = String(value).trim().length;
                        if (
                          trimmedLength < PROMO_CODE_MIN_LENGTH ||
                          trimmedLength > PROMO_CODE_MAX_LENGTH
                        ) {
                          return Promise.reject(
                            new Error(
                              `Promo code must be between ${PROMO_CODE_MIN_LENGTH} and ${PROMO_CODE_MAX_LENGTH} characters`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter promo code"
                    className="rounded-lg"
                    style={{ textTransform: "uppercase" }}
                    disabled={isEditMode}
                    maxLength={PROMO_CODE_MAX_LENGTH}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={<span className="font-semibold">Status</span>}
                  name="is_active"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="success" text="Active" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="Inactive" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  label={<span className="font-semibold">Description</span>}
                  name="promo_description"
                  rules={[{ validator: noWhitespaceValidator }]}
                >
                  <Input.TextArea
                    maxLength={255}
                    rows={3}
                    placeholder="Enter promo description"
                    className="rounded-lg"
                    showCount
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Discount Configuration */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <PercentageOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Discount Configuration
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  label={<span className="font-semibold">Promo Type</span>}
                  name="promo_type"
                  rules={[
                    { required: true, message: "Please select a promo type" },
                  ]}
                >
                  <Radio.Group
                    onChange={() => {
                      form.setFieldsValue({
                        promo_percent: undefined,
                        promo_fixed_amount: undefined,
                        promo_max_discount_amount: undefined,
                      });
                    }}
                  >
                    <Radio value="discount_based">Discount Based (%)</Radio>
                    <Radio value="amount_based">Amount Based (Fixed)</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            {isDiscountBased && (
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span className="font-semibold">Discount Percentage</span>
                    }
                    name="promo_percent"
                    rules={[
                      {
                        required: true,
                        message: "Please enter discount percentage",
                      },
                      {
                        validator: (_, value) => {
                          if (value == null || value === "") return Promise.resolve();
                          if (Number(value) <= 0 || Number(value) > 100) {
                            return Promise.reject(
                              new Error("Percentage must be greater than 0 and less than or equal to 100")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={1}
                      max={100}
                      suffix="%"
                      placeholder="Enter percentage"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span className="font-semibold">Max Discount Amount</span>
                    }
                    name="promo_max_discount_amount"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value == null || value === "") return Promise.resolve();
                          if (Number(value) < 0)
                            return Promise.reject(
                              new Error("Must be non-negative")
                            );
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      precision={2}
                      step={0.01}
                      placeholder="Enter max discount cap"
                      className="rounded-lg"
                      prefix={<DollarOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {isAmountBased && (
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={<span className="font-semibold">Fixed Amount</span>}
                    name="promo_fixed_amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter fixed amount",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      precision={2}
                      step={0.01}
                      placeholder="Enter fixed amount"
                      className="rounded-lg"
                      prefix={<DollarOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span className="font-semibold">
                      Min Transaction Amount
                    </span>
                  }
                  name="promo_min_txn_amount"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value == null || value === "") return Promise.resolve();
                        if (Number(value) < 0)
                          return Promise.reject(
                            new Error("Must be non-negative")
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    precision={2}
                    step={0.01}
                    placeholder="Enter minimum transaction amount"
                    className="rounded-lg"
                    prefix={<DollarOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Usage Limits */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <UserOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Usage Limits
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={<span className="font-semibold">Per User Limit</span>}
                  name="promo_per_user_limit"
                  extra={
                    isEditMode
                      ? `Can only be increased (current: ${savedLimits.promo_per_user_limit})`
                      : undefined
                  }
                  rules={[
                    { required: true, message: "Please enter per user limit" },
                    {
                      validator: (_, value) => {
                        if (value == null || value === "") return Promise.resolve();
                        if (Number(value) <= 0) {
                          return Promise.reject(
                            new Error("Per user limit must be greater than 0")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      validator: (_, value) => {
                        if (!isEditMode) return Promise.resolve();
                        const next = value ?? 0;
                        if (next < savedLimits.promo_per_user_limit) {
                          return Promise.reject(
                            new Error(
                              `Per user limit can only be increased (current: ${savedLimits.promo_per_user_limit})`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={isEditMode ? Math.max(1, savedLimits.promo_per_user_limit) : 1}
                    placeholder="Enter per user limit"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span className="font-semibold">Max Total Redeems</span>
                  }
                  name="promo_max_redeems"
                  extra={
                    isEditMode
                      ? `Can only be increased (current: ${savedLimits.promo_max_redeems})`
                      : undefined
                  }
                  rules={[
                    { required: true, message: "Please enter max redeems" },
                    {
                      validator: (_, value) => {
                        if (value == null || value === "") return Promise.resolve();
                        if (Number(value) <= 0) {
                          return Promise.reject(
                            new Error("Max redeems must be greater than 0")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      validator: (_, value) => {
                        if (!isEditMode) return Promise.resolve();
                        const next = value ?? 0;
                        if (next < savedLimits.promo_max_redeems) {
                          return Promise.reject(
                            new Error(
                              `Max redeems can only be increased (current: ${savedLimits.promo_max_redeems})`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={isEditMode ? Math.max(1, savedLimits.promo_max_redeems) : 1}
                    placeholder="Enter max redeems"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span className="font-semibold">Target Audience</span>
                  }
                  name="target_audience"
                  initialValue="ALL_USER"
                  extra="First-time users are those who have never held a subscription."
                  rules={[
                    { required: true, message: "Please select target audience" },
                  ]}
                >
                  <Select placeholder="Select target audience" className="rounded-lg">
                    <Option value="ALL_USER">All users</Option>
                    <Option value="FIRST_TIME_USER">First-time users only</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span className="font-semibold">Show In Catalogue</span>
                  }
                  name="catelog_enabled"
                  initialValue={false}
                  extra="Badges this promo's plan and plan type with 'Offer Available' on the subscription page, for the target audience above. Display only — it does not change who may redeem the code."
                >
                  <Select placeholder="Select" className="rounded-lg">
                    <Option value={false}>No</Option>
                    <Option value={true}>Yes</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Countries & Validity */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <GlobalOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Countries & Validity
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={24} lg={12}>
                <Form.Item
                  label={
                    <span className="font-semibold">Applicable Countries</span>
                  }
                  name="country_ids"
                  rules={[
                    {
                      required: true,
                      type: "array",
                      min: 1,
                      message: "Please select at least one country",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select countries"
                    showSearch
                    allowClear
                    className="rounded-lg"
                    size="large"
                    options={countries}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  label={<span className="font-semibold">Plan</span>}
                  name="fk_plan_id"
                  rules={[{ required: true, message: "Please select a plan" }]}
                >
                  <Select
                    placeholder="Select plan"
                    showSearch
                    allowClear
                    className="rounded-lg"
                    size="large"
                    options={planOptions}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  label={<span className="font-semibold">Plan Type</span>}
                  name="fk_plan_type_id"
                  rules={[{ required: true, message: "Please select a plan type" }]}
                >
                  <Select
                    placeholder="Select plan type"
                    showSearch
                    allowClear
                    className="rounded-lg"
                    size="large"
                    options={planTypeOptions}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>

            </Row>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={<span className="font-semibold">Start Date</span>}
                  name="start"
                  rules={[
                    { required: true, message: "Please select a start date" },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format={DATE_FORMAT}
                    placeholder="Select start date"
                    className="rounded-lg"
                    size="large"
                    onChange={() => form.validateFields(["expiration"])}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={<span className="font-semibold">Expiration Date</span>}
                  name="expiration"
                  rules={[
                    { required: true, message: "Please select an expiration date" },
                    { validator: validateExpirationDate },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format={DATE_FORMAT}
                    placeholder="Select expiration date"
                    className="rounded-lg"
                    size="large"
                    disabledDate={disabledExpirationDate}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div
            className="flex flex-wrap justify-end gap-3 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space wrap size="middle">
              <Button
                onClick={onReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={onGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {loading ? "Saving..." : "Save Promo Code"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default PromoForm;
